import React from 'react';

import { TextInput, UnstyledButton } from '@mantine/core';
import { Pencil } from 'lucide-react';

const s = {
  wrap: 'flex items-center gap-2 px-4 py-2 text-sm',
  button: 'flex items-center gap-2 bg-transparent border-none p-0 cursor-pointer text-left',
  placeholder: 'text-sm text-[#6b7772]',
  value: 'text-sm text-foreground',
  icon: 'w-3.5 h-3.5 text-[#8a938e]',
} as const;

interface ExportDescriptionProps {
  description: string;
  onChange: (description: string) => void;
}

/**
 * The "Add a description for this export" line under the tab strip.
 *
 * Maps to `Export.description`, which the backend already stores and `PATCH /export/{id}/`
 * already accepts — so wiring this to the API is a single mutation call, no model change.
 * Click the text or the pencil to edit; Enter or blur commits, Escape cancels.
 */
export function ExportDescription({ description, onChange }: ExportDescriptionProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(description);

  // Re-sync when switching tabs while not mid-edit.
  const lastDescription = React.useRef(description);
  if (lastDescription.current !== description && !isEditing) {
    lastDescription.current = description;
    setDraft(description);
  }

  const commit = React.useCallback(() => {
    setIsEditing(false);
    if (draft !== description) {
      onChange(draft);
    }
  }, [draft, description, onChange]);

  if (isEditing) {
    return (
      <div className={s.wrap}>
        <TextInput
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          size="xs"
          w={320}
          value={draft}
          placeholder="Add a description for this export"
          aria-label="Export description"
          onChange={(event) => setDraft(event.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commit();
            } else if (event.key === 'Escape') {
              setDraft(description);
              setIsEditing(false);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className={s.wrap}>
      <UnstyledButton className={s.button} onClick={() => setIsEditing(true)}>
        <span className={description ? s.value : s.placeholder}>{description || 'Add a description for this export'}</span>
        <Pencil className={s.icon} />
      </UnstyledButton>
    </div>
  );
}
